import { Image, Skeleton, Tag } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useGetAttendanceDetailsQuery } from "../../../../../../app/features/employee/emp-attendance/emp-attendance.api";
import userPlaceholder from "../../../../../../assets/images/attendanceUser.jpg";
import CustomDrawer from "../../../../../../components/CustomDrawer";
import { normalizeColumnValue } from "@/utils";
import { addHttps, firstChar } from "../../../../../../utils/string.utils";
import { formatDate } from '@/utils';

export default function AttendanceDetails({
  open,
  handleClose,
  data: id,
}: {
  data?: any;
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const { data, isLoading, isFetching } = useGetAttendanceDetailsQuery(id, {
    skip: !id,
  });

  const [imageLoading, setImageLoading] = useState(true);
  // const [imageError, setImageError] = useState(false);
  const imageError = false;

  const attendanceData = data?.data;

  // Determine status tag color and text
  const getStatusConfig = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "on_time":
      case "ontime":
        return {
          color: "#0BB449",
          text: "On Time",
        };
      case "late":
        return {
          color: "#F59E0B",
          text: "Late",
        };
      case "absent":
        return {
          color: "#EF4444",
          text: "Absent",
        };
      default:
        return {
          color: "#6B7280",
          text: status || "N/A",
        };
    }
  };

  const statusConfig = getStatusConfig(attendanceData?.checkInStatus);

  // Format time
  const formatTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format("hh:mm A");
  };

  // Get first image from attachment array
  const faceRecognitionImage =
    attendanceData?.check_in_attachment?.[0] || userPlaceholder;
  // console.log(faceRecognitionImage);

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={"Attendance Details"}
    >
      {isLoading || isFetching ? (
        <div className="w-full max-w-4xl mx-auto p-3">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between border-b border-gray-700 pb-4">
            <div className="flex items-center gap-4">
              <Skeleton.Avatar active size={56} shape="circle" />
              <div className="flex flex-col gap-2">
                <Skeleton.Button active size="small" style={{ width: 80 }} />
                <Skeleton.Button active size="small" style={{ width: 150 }} />
                <Skeleton.Button active size="small" style={{ width: 180 }} />
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Skeleton.Input active size="small" style={{ width: 150 }} />
              <Skeleton.Input active size="small" style={{ width: 150 }} />
            </div>
          </div>

          {/* Body Skeleton */}
          <div className="flex items-center flex-wrap gap-6 mt-8">
            {/* Image Skeleton */}
            <div className="flex-shrink-0 w-full sm:w-auto">
              <Skeleton.Image
                active
                style={{ width: 240, height: 240, borderRadius: 12 }}
              />
            </div>

            {/* Details Skeleton */}
            <div className="flex-1 flex flex-col gap-3 w-full sm:w-auto">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center gap-8"
                >
                  <Skeleton.Input active size="small" style={{ width: 120 }} />
                  <Skeleton.Input active size="small" style={{ width: 150 }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-4xl mx-auto p-3">
          {/* Header Section */}
          <div className="flex items-center justify-between border-b border-gray-700 pb-4">
            <div className="flex items-center gap-4">
              <span className="size-14 inline-flex justify-center items-center rounded-full border-[0.86px] font-semibold text-lg dark:bg-gray-300 dark:border-gray-200 dark:text-theme-gray-800 border-teal-500">
                {firstChar(attendanceData?.name || "N/A")}
              </span>
              <div>
                <p className="text-xs text-gray-400">
                  ID #{attendanceData?.employee_id || "N/A"}
                </p>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-white">
                    {attendanceData?.name || "N/A"}
                  </h2>
                </div>
                <p className="text-sm text-gray-400">
                  {normalizeColumnValue(attendanceData?.email || "N/A")}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <p className="text-sm text-gray-400">
                Employee ID :{" "}
                <span className="text-gray-300">
                  {normalizeColumnValue(attendanceData?.employee_id || "N/A")}
                </span>
              </p>

              <p className="text-sm text-gray-400">
                Assign To :{" "}
                <span className="text-gray-300">
                  {normalizeColumnValue(attendanceData?.manager_name || "N/A")}
                </span>
              </p>
            </div>
          </div>

          {/* Body Section */}
          <div className="flex items-center flex-wrap gap-6 mt-8">
            {/* Left Image Section */}
            <div className="flex-shrink-0 w-full sm:w-auto">
              <div className="text-left justify-start text-white text-base font-normal mb-4">
                Face Recognition
              </div>
              <div className="w-60 h-60 rounded-xl overflow-hidden bg-gray-800 relative">
                {imageLoading && !imageError && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <Skeleton.Image
                      active
                      style={{ width: 240, height: 240, borderRadius: 12 }}
                    />
                  </div>
                )}
                {imageError ? (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <span>Image not available</span>
                  </div>
                ) : (
                  <Image
                    src={addHttps(faceRecognitionImage)}
                    alt="Employee Face Recognition"
                    className="w-full h-full object-cover"
                    // preview={{
                    //   mask: (
                    //     <div className="text-white text-sm">Click to zoom</div>
                    //   ),
                    // }}
                    onLoad={() => setImageLoading(false)}
                    onError={() => {
                      setImageLoading(false);
                      // setImageError(true);
                    }}
                    style={{
                      opacity: imageLoading ? 0 : 1,
                      transition: "opacity 0.3s",
                    }}
                  />
                )}
              </div>
            </div>

            {/* Right Details Section */}
            <div className="flex-1 flex flex-col gap-3 text-sm w-full sm:w-auto">
              <LabelValue
                label="Date"
                value={
                  attendanceData?.date
                    ? formatDate(attendanceData.date, "YYYY-MM-DD")
                    : "N/A"
                }
              />
              <LabelValue
                label="Check In Time"
                value={
                  attendanceData?.checkInTime
                    ? formatTime(attendanceData?.checkInTime)
                    : "N/A"
                }
              />
              <LabelValue
                label="Check Out Time"
                value={
                  attendanceData?.checkOutTime
                    ? formatTime(attendanceData?.checkOutTime)
                    : "N/A"
                }
              />

              <div className="flex justify-between items-center">
                <div className="text-white/70 text-base font-light">
                  Status:
                </div>
                <Tag
                  className="mt-1 px-3 py-0.5 rounded-md border !mr-0 !bg-transparent text-sm font-bold"
                  style={{
                    borderColor: statusConfig.color,
                    color: statusConfig.color,
                  }}
                >
                  {statusConfig.text}
                </Tag>
              </div>

              <LabelValue
                label="Late Reason"
                value={attendanceData?.lateNote || "N/A"}
              />
            </div>
          </div>
        </div>
      )}
    </CustomDrawer>
  );
}

interface LabelValueProps {
  label: string;
  value: string;
}

/* Reusable label-value component */
function LabelValue({ label, value }: LabelValueProps) {
  return (
    <div className="flex justify-between items-center gap-6">
      <div className=" text-white/70 text-base font-light">{label}:</div>
      <div className="text-white text-base font-medium  text-end">{value}</div>
    </div>
  );
}
